import http from './base/http';
import Config from '@/config';
import {
  Zodiac,
  CURRENCY_DEFAULT,
  LOCALE_CODE_DEFAULT,
} from '@/helper/constants';
import { coustomInterval } from '@/helper/util';
import { AddonsLog } from '@/helper/log';
import { handleAACheckoutError } from '@/helper/error-handler';
import { ZodiacAuth } from '@/api/common';
import { webStore } from '@/store/base';

const userContext = {
  currency: CURRENCY_DEFAULT,
  geoId: Zodiac.GEO_ID,
  locale: LOCALE_CODE_DEFAULT,
};

export async function getConfig() {
  const platform = webStore.paramManager.getParam('platform');
  return Config.ZODIAC[
    (platform as string).toUpperCase() as 'ALIPAY' | 'WECHAT'
  ];
}

export const checkPriceWithoutPolling = async <T>(
  tripId,
  airlineProfile,
  tokenInfo: TokensExternalAuth,
) => {
  const zodiacConfig = await getConfig();
  const url = `${zodiacConfig.SERVICE_ENDPOINT}/fp/checkout/v3/page-data`;

  const headers = {
    channel_hash: zodiacConfig.CHANNEL_HASH,
    'Ga-Id': generateGaId(tokenInfo.userId),
  };

  const data = {
    userContext,
    flightDetails: {
      tripId: [`${tripId}`],
      airlineProfile: airlineProfile,
    },
    validateFlights: false,
    sso: {
      accessToken: tokenInfo.accessToken,
      refreshToken: tokenInfo.refreshToken,
      id: tokenInfo.userId,
    },
  };

  return http.post<T>(url, data, { headers });
};

export const checkPrice = async <T>(
  tripId,
  airlineProfile,
  tokenInfo: TokensExternalAuth,
) => {
  let count = 0;
  let isValid = false;
  let pageData = null;
  const polling = async (stop) => {
    pageData = await checkPriceWithoutPolling(
      tripId,
      airlineProfile,
      tokenInfo,
    );
    count++;

    if (
      pageData.flightDetails &&
      pageData.flightDetails.requiresValidation === false
    ) {
      isValid = true;
      stop();
    } else if (count == Zodiac.MAX_COUNT_POLLING_CHECKP_PRICE) {
      stop();
    }

    return pageData;
  };
  await coustomInterval(polling, Zodiac.GAP_POLLING_CHECKP_PRICE).start();

  return {
    isValid,
    pageData,
  };
};

export const generateGaId = (userId) => `GA-WMP-${userId}`;

export const checkoutV3 = async <T>(data) => {
  const url = `${Config.BFF_SERVICE.URL}/hainan-bff/zodiac/checkout-initiate`;
  const userId = data.loggedInUserInfo?.ssoDetails?.userId;

  const zodiacToken = await ZodiacAuth.getFullToken();
  const zodiacConfig = await getConfig();
  const headers = {
    'zodiac-authorization': zodiacToken,
    'zodiac-channel_hash': zodiacConfig.CHANNEL_HASH,
    'zodiac-user-type': Zodiac.USER_TYPE_MEMBER,
    'zodiac-ga-Id': generateGaId(userId),
  };

  try {
    const res = await http.post<BFFResponse<any>>(url, data, { headers });
    AddonsLog.logCheckoutSuccess({ url, headers, data }, { data: res });
    return res;
  } catch (error) {
    AddonsLog.logCheckoutFailure({ url, headers, data }, error?.response);
    handleAACheckoutError(error);
  }
};

const getAncillaryRequestData = (data, includeAncillary: Array<string>) => {
  return {
    userContext: {
      currency: 'CNY',
      geoId: 'CAN',
      locale: 'zh-cn',
    },
    flightDetails: {
      tripId: data.tripsInfo.tripIds,
      airlineProfile: data.tripsInfo.airlineProfile,
      departCabinType: data.tripsInfo.departCabinClass,
      returnCabinType: data.tripsInfo.returnCabinClass,
    },
    validateFlights: false,
    sso: {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      userId: data.userId,
    },
    includeAncillary,
  };
};

export const getAncillaryItem = async <T>(
  data: any,
  items: string[],
  customFlag?: string,
) => {
  const url = `${Config.BFF_SERVICE.URL}/hainan-bff/zodiac/checkout-page-data?include_list=ancillary,locale`;

  const zodiacToken = await ZodiacAuth.getFullToken();
  const zodiacConfig = await getConfig();
  const headers = {
    'zodiac-authorization': zodiacToken,
    'zodiac-channel_hash': zodiacConfig.CHANNEL_HASH,
    'zodiac-user-type': Zodiac.USER_TYPE_MEMBER,
    'zodiac-ga-Id': generateGaId(data.userId),
  };

  const requestData = getAncillaryRequestData(data, items);
  const config: any = { headers };

  // extract request flag from items[0] or use custom flag
  const requestFlag = customFlag || items[0];
  config._requestFlag = requestFlag;

  const isFlightDetail = items.length === 0;
  try {
    const res = await http.post<BFFResponse<any>>(url, requestData, config);

    const flightDetailsHasError =
      !res.data.flightDetails || res.data.flightDetails.error;

    if (isFlightDetail && !flightDetailsHasError) {
      AddonsLog.logFlightDetailsSuccess(
        { url, headers, data: requestData },
        { data: res.data },
      );
    } else if (isFlightDetail && flightDetailsHasError) {
      AddonsLog.logFlightDetailsFailure(
        { url, headers, data: requestData },
        { data: res.data },
      );
    }

    return res.data;
  } catch (error) {
    if (isFlightDetail) {
      AddonsLog.logFlightDetailsFailure(
        { url, headers, data: requestData },
        error?.response,
      );
    }

    throw error;
  }
};

export const getAncillaryBaggage = async <T>(data) => {
  return getAncillaryItem<T>(data, ['baggage']);
};

export const getAncillaryInsurance = async <T>(data) => {
  return getAncillaryItem<T>(data, ['insurance']);
};

export const getAncillaryWheelchair = async <T>(data) => {
  return getAncillaryItem<T>(data, ['wheelchair']);
};

export const checkout = async <T>(data) => {
  const zodiacConfig = await getConfig();
  const url = `${zodiacConfig.SERVICE_ENDPOINT}/fp/checkout/v1/initiate`;

  const headers = {
    channel_hash: zodiacConfig.CHANNEL_HASH,
  };

  const res = await http.post<T>(url, data, { headers });
  return res.data;
};

export const getPageData = async <T>(
  tripsInfo: TripsInfo,
  tokenInfo: TokensExternalAuth,
  includeAncillary: AACheckout.AncillaryType[] = [],
  includeList: string[] = [],
) => {
  const url = `${Config.BFF_SERVICE.URL}/hainan-bff/zodiac/checkout-page-data${
    includeList.length > 0 ? `?include_list=${includeList.join(',')}` : ''
  }`;

  const zodiacToken = await ZodiacAuth.getFullToken();
  const zodiacConfig = await getConfig();
  const headers = {
    'zodiac-authorization': zodiacToken,
    'zodiac-channel_hash': zodiacConfig.CHANNEL_HASH,
    'zodiac-user-type': Zodiac.USER_TYPE_MEMBER,
    'zodiac-ga-Id': generateGaId(tokenInfo.userId),
  };

  const data = {
    userContext,
    flightDetails: {
      tripId: tripsInfo.tripIds,
      airlineProfile: tripsInfo.airlineProfile,
      departCabinType: tripsInfo.departCabinClass,
      returnCabinType: tripsInfo.returnCabinClass,
    },
    includeAncillary,
    validateFlights: false,
    sso: {
      accessToken: tokenInfo.accessToken,
      refreshToken: tokenInfo.refreshToken,
      userId: tokenInfo.userId,
    },
  };

  const config: any = { headers };
  // set request flag based on includeAncillary
  config._requestFlag = includeAncillary[0] || 'flightDetails';

  const isFlightDetail = isRequestFlightDetail(includeList);
  try {
    const res = await http.post<BFFResponse<any>>(url, data, config);

    if (isFlightDetail) {
      AddonsLog.logFlightDetailsSuccess(
        { url, headers, data },
        { data: res.data },
      );
    }

    return res.data;
  } catch (error) {
    if (isFlightDetail) {
      AddonsLog.logFlightDetailsFailure(
        { url, headers, data },
        error?.response,
      );
    }

    throw error;
  }
};

const isRequestFlightDetail = (includeList = []) => {
  return includeList.length === 0;
};
